import { FC } from "react";
import { useTranslation } from "react-i18next";
import { useFormContext } from "react-hook-form";

// Constants
import { allergenOptions, cuisineOptions, dietOptions, recipeCategoryOptions } from "@/_constants/recipe";

// Services and interfaces
import { IRecipeForm } from "@/interfaces/recipe/recipe_form";

// Components
import { Box } from "@mui/material";
import ImageInput from "@/components/image_input";
import MultiselectInput from "../multiselect_input";
import NumberInput from "@/components/number_input";
import SelectInput from "@/components/select_input";
import IteratorInput from "@/components/iterator_input";
import ImagePlaceholder from '@/assets/recipes/recipe_placeholder.png'


interface _DetailsPaneProps {
    readonly: boolean;
    isLoading: boolean;
    recipeImage?: string|null;
}

const DetailsPane: FC<_DetailsPaneProps> = ({
    readonly,
    isLoading,
    recipeImage
}) => {

    const { t } = useTranslation()
    const { control, formState: { errors } } = useFormContext<IRecipeForm>();
    
    return (
        <Box 
            width="320px"
            p={2}
            boxSizing="border-box"
            bgcolor="var(--layer-01)"
            border="solid 1px var(--border-subtle-01)" 
            borderRadius="6px"
            >

            <ImageInput 
                name='image'
                control={control}
                label={t('components.recipeBuilder.image')}
                defaultImage={recipeImage ?? ImagePlaceholder}
                errorMessage={t('inputs.errors.recipeImage')}
                hasError={!!errors.image}
                isLoading={isLoading}
                disabled={readonly}
                />

            <IteratorInput
                name="servings"
                control={control}
                label={t('components.recipeBuilder.servings')}
                isLoading={isLoading}
                allowZero={false}
                maxValue={25}
                disabled={readonly}
                />

            <NumberInput
                name="prep_time"
                control={control}
                label={t('components.recipeBuilder.prepTime')}
                suffixText="minutes"
                maxLength={3}
                isLoading={isLoading}
                disabled={readonly}
                />

            <NumberInput
                name="cook_time"
                control={control}
                label={t('components.recipeBuilder.cookTime')}
                suffixText="minutes"
                maxLength={3}
                isLoading={isLoading}
                disabled={readonly}
                />

            <MultiselectInput
                name="categories"
                control={control}
                label={t('components.recipeBuilder.category')}
                itemKey="value"
                items={recipeCategoryOptions}
                itemsLoading={isLoading}
                isLoading={isLoading}
                disabled={readonly}
                />

            <MultiselectInput
                name="diet"
                control={control}
                label={t('components.recipeBuilder.diet')}
                itemKey="value"
                items={dietOptions}
                itemsLoading={isLoading}
                isLoading={isLoading}
                disabled={readonly}
                />

            <MultiselectInput
                name="allergens"
                control={control}
                label={t('components.recipeBuilder.contains')}
                itemKey="value"
                itemLabel="label"
                items={allergenOptions}
                itemsLoading={isLoading}
                isLoading={isLoading}
                disabled={readonly}
                />

            <SelectInput
                name="cuisine"
                control={control}
                label={t('components.recipeBuilder.cuisine')}
                itemKey="value"
                items={cuisineOptions}
                itemsLoading={isLoading}
                isLoading={isLoading}
                disabled={readonly}
                />

        </Box>
    )
}

export default DetailsPane;